import { toast } from "vue-sonner";
import i18n from "@/shared/config/i18n";
import { centrifugeManager } from "@/shared/lib/centrifuge";
import {
  useNotificationStore,
  NOTIFICATIONS_CHANNEL_PREFIX,
  wsToNotification,
} from "@/entities/notification";

/**
 * Mounts the realtime pipeline for the given user. Returns a stop() function.
 *
 *  - Subscribes to Centrifuge channel `notifications/${userUuid}`.
 *  - Each event: ingests into store + fires toast (preserves existing UX).
 *  - On `visibilitychange` → resync counter + first page.
 *  - On `online` → resync counter + first page.
 *
 *  Centrifuge itself handles auto-reconnect with backoff; resync covers
 *  the "missed while offline" case.
 *
 * @param {{ userUuid: string }} opts
 * @returns {() => void}
 */
export function useNotificationsRealtime({ userUuid }) {
  if (!userUuid) return () => {};

  const store = useNotificationStore();
  store.bootstrap();

  centrifugeManager.init();

  const channel = `${NOTIFICATIONS_CHANNEL_PREFIX}${userUuid}`;
  const unsubscribe = centrifugeManager.subscribe(channel, (data) => {
    const normalized = wsToNotification(data);
    if (!normalized) return;

    store.ingestRealtime(data);

    // Toast preserved — same behavior as legacy usePushNotifications
    if (normalized.title) {
      toast.success(normalized.title, {
        description: normalized.body || undefined,
        duration: 15000,
      });
    }
  });

  const onVisible = () => {
    if (document.visibilityState !== "visible") return;
    store.fetchCounter();
    store.fetchList({ page: 1 });
    store.fetchCategories();
  };
  const onOnline = () => {
    store.fetchCounter();
    store.fetchList({ page: 1 });
    store.fetchCategories();
  };
  const onLocaleChange = () => {
    // Locale changed in another tab or via switcher — refetch with new Accept-Language
    store.fetchList({ page: 1 });
    store.fetchCategories();
  };

  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("online", onOnline);

  // i18n locale change (legacy mode: locale is a string, no built-in watcher)
  let lastLocale =
    (i18n.global && (i18n.global.locale?.value || i18n.global.locale)) || "";
  const localeWatchInterval = window.setInterval(() => {
    const current =
      (i18n.global && (i18n.global.locale?.value || i18n.global.locale)) || "";
    if (current && current !== lastLocale) {
      onLocaleChange();
      lastLocale = current;
    }
  }, 1500);

  return function stop() {
    try {
      unsubscribe?.();
    } catch (_) {
      /* noop */
    }
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("online", onOnline);
    window.clearInterval(localeWatchInterval);
  };
}
